import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { settingsApi } from '../../../api/endpoints/settings';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Alert';
import type { StoreSettings } from '../../../types/settings';

interface Props {
  settings?: StoreSettings;
  storeId: string;
}

export function TrackingTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const tracking = settings?.tracking ?? {};

  const { register, handleSubmit } = useForm({
    defaultValues: {
      facebookPixel: tracking.facebookPixel ?? '',
      googleAnalytics: tracking.googleAnalytics ?? '',
      googleAds: tracking.googleAds ?? '',
      tiktokPixel: tracking.tiktokPixel ?? '',
      snapchatPixel: tracking.snapchatPixel ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => settingsApi.updateTracking(storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('settingsTracking.saveSuccess'));
    },
    onError: () => toast('error', t('common.error')),
  });

  return (
    <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-6">
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold">{t('settingsTracking.title')}</h2>
        <p className="text-sm text-text-muted">
          Add tracking pixel IDs to enable conversion tracking and analytics on your storefront.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            id="facebookPixel"
            label={t('settingsTracking.facebookPixel')}
            placeholder="123456789012345"
            {...register('facebookPixel')}
          />
          <Input
            id="googleAnalytics"
            label={t('settingsTracking.googleAnalytics')}
            placeholder="G-XXXXXXXXXX"
            {...register('googleAnalytics')}
          />
          <Input
            id="googleAds"
            label={t('settingsTracking.googleAds')}
            placeholder="AW-XXXXXXXXX"
            {...register('googleAds')}
          />
          <Input
            id="tiktokPixel"
            label={t('settingsTracking.tiktokPixel')}
            placeholder="XXXXXXXXXXXXXXXXXX"
            {...register('tiktokPixel')}
          />
          <Input
            id="snapchatPixel"
            label={t('settingsTracking.snapchatPixel')}
            placeholder="XXXXXXXX-XXXX-XXXX-XXXX"
            {...register('snapchatPixel')}
          />
        </div>
      </section>

      <div className="flex justify-end">
        <Button type="submit" isLoading={mutation.isPending}>{t('common.save')}</Button>
      </div>
    </form>
  );
}
